/** GPU Motion Blur — single-pass directional averaging.
 *
 *  Fragment-shader port of `lib/filters/motion-blur.ts`. Per output
 *  pixel, we sample N points along a line of length `distance`
 *  centered on the pixel, oriented at `angle` degrees, and average
 *  the colors. This is the only blur in this batch that's
 *  *single-pass* — the convolution is along an arbitrary direction
 *  rather than along an axis, so the separable trick that buys
 *  Gaussian / Box their 2× speedup doesn't apply.
 *
 *  Sample count, start offset, and step delta are all pre-computed
 *  CPU-side from the user's `angle` + `distance` so the shader is
 *  just an N-iteration loop. Matches the JS path's `samples`,
 *  `startX/Y`, `stepX/Y` derivation precisely.
 *
 *  Identity short-circuit at `distance < 0.5` mirrors the JS path
 *  (smaller distances round to 0 effective samples and are
 *  visually indistinguishable from source).
 *
 *  GPU loops with non-constant bounds are allowed in WebGL2 (GLSL
 *  ES 3.00) but compilers do better with a constant upper bound +
 *  a runtime cutoff. MAX_SAMPLES = 1024 covers distances up to
 *  ~1024 px — generous for any realistic motion-blur amount the
 *  editor's UI exposes.
 *
 *  ── Y-axis convention ─────────────────────────────────────────
 *  Direction-sensitive: `stepY = sin(angle) * distance / (N-1)` is
 *  in CPU top-down convention (positive Y = downward in DOM). The
 *  runtime's natural `vec2 px = v_uv * u_dims - 0.5` puts px.y in
 *  bottom-up coords, which flipped the effective Y direction —
 *  e.g. a user-set 45° angle blurred toward DOM-up-right on GPU vs
 *  DOM-down-right on CPU. Fix: compute px in top-down coords and
 *  flip Y when forming srcUv. See twirl.ts for the broader
 *  rationale. */

import type { MotionBlurValue } from '../../filters/motion-blur';
import { applyIdentityCopy } from '../identity-copy';
import { applyShader } from '../runtime';

const MAX_SAMPLES = 1024;

const MOTION_BLUR_FRAG = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_source;
uniform vec2 u_dims;
uniform vec2 u_step;    // per-sample pixel delta along motion vector
uniform vec2 u_start;   // first sample's offset from current pixel
uniform int u_samples;  // total sample count (>= 2)
out vec4 outColor;

void main() {
  // px in CPU-aligned top-down coords so u_start.y / u_step.y
  // (computed from sin(angle), top-down convention) point the same
  // direction the CPU loop does.
  float pxX = v_uv.x * u_dims.x - 0.5;
  float pxY = (1.0 - v_uv.y) * u_dims.y - 0.5;
  vec3 sum = vec3(0.0);
  vec4 center = texture(u_source, v_uv);
  for (int s = 0; s < ${MAX_SAMPLES}; s++) {
    if (s >= u_samples) break;
    // Match the JS: round to nearest integer pixel then clamp.
    float fs = float(s);
    float sxClamped = clamp(floor(pxX + u_start.x + u_step.x * fs + 0.5), 0.0, u_dims.x - 1.0);
    float syClamped = clamp(floor(pxY + u_start.y + u_step.y * fs + 0.5), 0.0, u_dims.y - 1.0);
    // syClamped is in top-down coords; flip Y when forming the
    // sampling UV so the UNPACK_FLIP_Y source is read at the
    // matching DOM row.
    vec2 uv = vec2(
      (sxClamped + 0.5) / u_dims.x,
      1.0 - (syClamped + 0.5) / u_dims.y
    );
    sum += texture(u_source, uv).rgb;
  }
  outColor = vec4(sum / float(u_samples), center.a);
}
`;

export function applyMotionBlurOnGpu(
  source: ImageData,
  target: HTMLCanvasElement,
  value: MotionBlurValue,
): boolean {
  const distance = Math.max(0, value.distance);
  if (distance < 0.5) return applyIdentityCopy(source, target);
  // Mirror the JS derivation exactly so byte parity holds.
  const half = distance / 2;
  const samples = Math.max(2, Math.round(distance) + 1);
  if (samples > MAX_SAMPLES) return false; // outside our budget — CPU fallback
  const rad = (value.angle * Math.PI) / 180;
  const stepX = (Math.cos(rad) * distance) / (samples - 1);
  const stepY = (Math.sin(rad) * distance) / (samples - 1);
  const startX = -Math.cos(rad) * half;
  const startY = -Math.sin(rad) * half;
  return applyShader({
    source,
    target,
    fragShaderSrc: MOTION_BLUR_FRAG,
    uniforms: {
      u_dims: { type: 'vec2', value: [source.width, source.height] },
      u_step: { type: 'vec2', value: [stepX, stepY] },
      u_start: { type: 'vec2', value: [startX, startY] },
      u_samples: { type: 'int', value: samples },
    },
  });
}
